import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";

import uniqBy from "lodash/uniqBy";
import { useCustomerIdGuard } from "../../../common/hooks/useCustomerIdGuard";
import { LIST_HEALTH_LEAVES } from "../../../common/operations/queries";
import { errorNotification } from "../../../common/variables/notification";
import client from "../../../configs/apolloClient";
import { useListenToDeviceDataChange } from "./useListenToDeviceDataChange";
import { useListenToNodeOnlineStatusChange } from "./useListenToNodeHealthDataChange";
import { useListenToServiceHealthDataChange } from "./useListenToServiceHealthDataChange";

interface IHealthStatus {
  status: boolean;
  uptime: string;
}

export interface IParsedNodeHealthCheckStatus extends IHealthStatus {
  nodeId: string;
  nodeName: string;
  isExpectedOnline?: boolean;
  reason?: string;
  assignedWatcher?: string;
  leafPk?: string;
  leafSk?: string;
  lastAliveEpochMs?: number;
}

export interface IParsedDeviceHealthCheckStatus extends IHealthStatus {
  deviceId: string;
  deviceName: string;
  isExpectedOnline?: boolean;
  reason?: string;
  assignedWatcher?: string;
  leafPk?: string;
  leafSk?: string;
  lastAliveEpochMs?: number;
}

export interface IParsedServiceHealthCheckStatus extends IHealthStatus {
  serviceType: string;
  serviceId: string;
  serviceLabel: string;
  isExpectedOnline?: boolean;
  reason?: string;
  assignedWatcher?: string;
  leafPk?: string;
  leafSk?: string;
  lastAliveEpochMs?: number;
}

export interface IParsedDeploymentHealthRow {
  locationId: string;
  locationName: string;
  nodes: IParsedNodeHealthCheckStatus[];
  devices: IParsedDeviceHealthCheckStatus[];
  services: IParsedServiceHealthCheckStatus[];
}

type HealthLeaf = {
  pk: string;
  sk: string;
  entity: "NODE" | "DEVICE" | "SERVICE";
  customerId: string;
  locationId: string;
  locationName: string;
  nodeId?: string;
  nodeName?: string;
  deviceId?: string;
  deviceName?: string;
  serviceId?: string;
  serviceType?: string;
  isOnline: boolean;
  lastSeenEpochMs?: number;
  isExpectedOnline?: boolean;
  reason?: string;
  assignedWatcher?: string;
};

interface IUseDeploymentHealthLeaves {
  rows?: IParsedDeploymentHealthRow[];
  loading: boolean;
  fetchingMore: boolean;
}

export const getServiceFromAcronym = (acronym: string) => {
  switch (acronym) {
    case "gl":
      return "Gas Leak";
    case "ll":
      return "Liquid Leak";
    case "hh":
    case "ss":
    case "pd":
      return "Hard Hat";
    case "tl":
      return "Tank Level";
    case "fs":
      return "Fire/Smoke";
    case "gg":
      return "Gate Guard";
    // case "ss":
    //   // return "Safety and Security";
    // case "pd":
    //   // return "Personnel Detection";
    default:
      return "UNKNOWN";
  }
};

export const useDeploymentHealthLeaves = (
  pageSize = 100,
  location?: string
): IUseDeploymentHealthLeaves => {
  const selectedCustomerId = useCustomerIdGuard();

  const { data: deviceHealthData, isOnline: isDeviceOnline } =
    useListenToDeviceDataChange();
  const { data: nodeHealthData } = useListenToNodeOnlineStatusChange();
  const { data: serviceHealthData, isOnline: isServiceOnline } =
    useListenToServiceHealthDataChange();

  const nextTokenRef = useRef<string | null>(null);

  const handleRequestError = async (): Promise<void> => {
    await client.cache.reset();

    errorNotification();
  };

  const [listLeaves, { data, loading, fetchMore }] = useLazyQuery(
    LIST_HEALTH_LEAVES,
    {
      fetchPolicy: "network-only",
      onError: handleRequestError,
    }
  );
  const [fetchingMore, setFetchingMore] = useState(false);

  const [allLeaves, setAllLeaves] = useState<HealthLeaf[]>([]);

  // fetch initial data
  useEffect((): void => {
    if (!selectedCustomerId) return;

    nextTokenRef.current = null;

    listLeaves({
      variables: {
        customerId: selectedCustomerId,
        limit: pageSize,
        location: location || null,
      },
    });
  }, [selectedCustomerId, location, pageSize, listLeaves]);

  // fetch more pages if available
  useEffect(() => {
    if (!data?.listHealthLeaves?.nextToken) return;

    const fetchAllPages = async () => {
      setFetchingMore(true);
      let currentNextToken = data?.listHealthLeaves?.nextToken || "";

      try {
        while (currentNextToken) {
          const response = await fetchMore({
            variables: {
              limit: pageSize,
              customerId: selectedCustomerId,
              location: location || null,
              nextToken: currentNextToken,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult) {
                return prev;
              }

              const leaves = uniqBy(
                [
                  ...(prev.listHealthLeaves?.items ?? []),
                  ...(fetchMoreResult.listHealthLeaves?.items ?? []),
                ],
                item => item?.sk
              );

              return {
                listHealthLeaves: {
                  ...fetchMoreResult.listHealthLeaves,
                  items: leaves,
                },
              };
            },
          });

          currentNextToken = response.data?.listHealthLeaves?.nextToken || "";
        }
      } catch (error) {
        console.error("Error fetching additional device pages:", error);

        errorNotification();
      } finally {
        setFetchingMore(false);
      }
    };

    fetchAllPages();
  }, [data?.listHealthLeaves?.nextToken, fetchMore, pageSize, location]);

  useEffect(() => {
    if (!selectedCustomerId || !data) return;

    const pollInterval = setInterval(() => {
      listLeaves({
        variables: {
          customerId: selectedCustomerId,
          limit: pageSize,
          location: location || null,
        },
      });
    }, 2 * 60 * 1000); // check last alive status every 2 minutes

    return () => clearInterval(pollInterval);
  }, [selectedCustomerId, location, pageSize, listLeaves]);

  useEffect(() => {
    if (data?.listHealthLeaves?.items) {
      setAllLeaves(data.listHealthLeaves.items as HealthLeaf[]);
    }
  }, [data?.listHealthLeaves?.items]);

  // live updates via subscriptions
  useEffect(() => {
    if (
      !deviceHealthData?.listenToDeviceData &&
      !nodeHealthData?.listenToNodeOnlineStatus &&
      !serviceHealthData?.listenToServiceHealthData
    ) {
      return;
    }

    setAllLeaves(prev =>
      prev.map(leaf => {
        // node update
        if (
          leaf.entity === "NODE" &&
          leaf.nodeId &&
          leaf.nodeId === nodeHealthData?.listenToNodeOnlineStatus?.id
        ) {
          return {
            ...leaf,
            isOnline: !!nodeHealthData?.listenToNodeOnlineStatus?.isOnline,
          };
        }

        // device update
        if (
          leaf.entity === "DEVICE" &&
          leaf.deviceId &&
          leaf.deviceId === deviceHealthData?.listenToDeviceData?.id
        ) {
          return { ...leaf, isOnline: isDeviceOnline };
        }

        // service update (match by canonical id)
        if (
          leaf.entity === "SERVICE" &&
          leaf.customerId &&
          leaf.serviceId &&
          serviceHealthData?.listenToServiceHealthData?.id &&
          `${leaf.customerId}#${leaf.serviceId}` ===
            serviceHealthData.listenToServiceHealthData.id
        ) {
          return { ...leaf, isOnline: isServiceOnline };
        }

        return leaf;
      })
    );
  }, [
    deviceHealthData,
    nodeHealthData,
    serviceHealthData,
    isDeviceOnline,
    isServiceOnline,
  ]);

  const rows = useMemo<IParsedDeploymentHealthRow[]>(() => {
    const byLocation: Record<
      string,
      {
        locationId: string;
        locationName: string;
        nodes: Record<string, IParsedNodeHealthCheckStatus>;
        devices: Record<string, IParsedDeviceHealthCheckStatus>;
        services: IParsedServiceHealthCheckStatus[];
      }
    > = {};

    for (const leaf of allLeaves ?? []) {
      const loc =
        byLocation[leaf.locationId] ??
        (byLocation[leaf.locationId] = {
          locationId: leaf.locationId,
          locationName: leaf.locationName,
          nodes: {},
          devices: {},
          services: [],
        });

      if (leaf.entity === "NODE" && leaf.nodeId && leaf.nodeName) {
        loc.nodes[leaf.nodeId] = {
          nodeId: leaf.nodeId,
          nodeName: leaf.nodeName,
          status: leaf.isOnline,
          uptime: "",
          isExpectedOnline: leaf.isExpectedOnline,
          reason: leaf.reason,
          assignedWatcher: leaf.assignedWatcher,
          leafPk: leaf.pk,
          leafSk: leaf.sk,
          lastAliveEpochMs: leaf.lastSeenEpochMs,
        };
      } else if (leaf.entity === "DEVICE" && leaf.deviceId && leaf.deviceName) {
        loc.devices[leaf.deviceId] = {
          deviceId: leaf.deviceId,
          deviceName: leaf.deviceName,
          status:
            leaf.isOnline ??
            (typeof leaf.lastSeenEpochMs === "number"
              ? Date.now() - leaf.lastSeenEpochMs <= 15 * 60 * 1000
              : false),
          uptime: "",
          isExpectedOnline: leaf.isExpectedOnline,
          reason: leaf.reason,
          assignedWatcher: leaf.assignedWatcher,
          leafPk: leaf.pk,
          leafSk: leaf.sk,
          lastAliveEpochMs: leaf.lastSeenEpochMs,
        };

        // It is possible node exists only through device/service leaves; ensure node placeholder
        if (leaf.nodeId && leaf.nodeName && !loc.nodes[leaf.nodeId]) {
          loc.nodes[leaf.nodeId] = {
            nodeId: leaf.nodeId,
            nodeName: leaf.nodeName,
            status: false,
            uptime: "",
            lastAliveEpochMs: leaf.lastSeenEpochMs,
          };
        }
      } else if (leaf.entity === "SERVICE" && leaf.serviceType) {
        let serviceType =
          leaf.serviceType !== "UNKNOWN"
            ? leaf.serviceType
            : getServiceFromAcronym(
                leaf.sk.split("#LEAF-S#")[1].split("_")[0].replace("S#", "")
              );

        // additional check for serviceType: No Personnel Detection or Safety and Security
        if (
          serviceType === "Personnel Detection" ||
          serviceType === "Safety and Security"
        ) {
          serviceType = "Hard Hat";
        }

        const deviceSuffix = leaf.deviceId?.split("DE#")[1];
        const serviceLabel = deviceSuffix
          ? `${serviceType}-${deviceSuffix}`
          : serviceType;

        loc.services.push({
          serviceId: leaf.sk.split("#LEAF-S#")[1],
          serviceType: serviceType,
          serviceLabel,
          status:
            leaf.isOnline ??
            (typeof leaf.lastSeenEpochMs === "number"
              ? Date.now() - leaf.lastSeenEpochMs <= 15 * 60 * 1000
              : false),
          uptime: "",
          isExpectedOnline: leaf.isExpectedOnline,
          reason: leaf.reason,
          assignedWatcher: leaf.assignedWatcher,
          leafPk: leaf.pk,
          leafSk: leaf.sk,
          lastAliveEpochMs: leaf.lastSeenEpochMs,
        });

        // ensure device and node placeholders exist for completeness
        if (leaf.deviceId && leaf.deviceName && !loc.devices[leaf.deviceId]) {
          loc.devices[leaf.deviceId] = {
            deviceId: leaf.deviceId,
            deviceName: leaf.deviceName ?? leaf.deviceId,
            status: false,
            uptime: "",
            lastAliveEpochMs: leaf.lastSeenEpochMs,
          };
        }

        if (leaf.nodeId && leaf.nodeName && !loc.nodes[leaf.nodeId]) {
          loc.nodes[leaf.nodeId] = {
            nodeId: leaf.nodeId,
            nodeName: leaf.nodeName,
            status: false,
            uptime: "",
            lastAliveEpochMs: leaf.lastSeenEpochMs,
          };
        }
      }
    }

    return Object.values(byLocation).map(loc => ({
      locationId: loc.locationId,
      locationName: loc.locationName,
      nodes: Object.values(loc.nodes),
      devices: Object.values(loc.devices),
      services: loc.services,
    }));
  }, [allLeaves]);

  return { rows, loading, fetchingMore };
};
